/**
 * Silver Rate Settings
 * Admin sets today's manual silver rates (per gram) for each purity.
 * There is no live silver feed — these rates drive the case forms
 * whenever Metal Type = Silver.
 */

import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, InputNumber, Space, message, Alert, Tag, Typography, Spin,
} from 'antd';
import { SaveOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchSilverRates, updateSilverRates } from '../../../services/silverPriceService';

const { Title, Text } = Typography;

export default function SilverRateSettings() {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const load = async (force = true) => {
    setLoading(true);
    const data = await fetchSilverRates(force);
    if (data) {
      setRows((data.rates || []).map((r, i) => ({ ...r, key: i })));
      setDate(data.date);
      setNotConfigured(data.notConfigured);
    } else {
      messageApi.error('Failed to load silver rates');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setRate = (key, perGram) => {
    setRows(prev => prev.map(r => r.key === key
      ? { ...r, perGram: perGram || 0, per10gram: Math.round((perGram || 0) * 10 * 100) / 100 }
      : r));
  };

  const setLabel = (key, field, value) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, [field]: value } : r));
  };

  const addRow = () => {
    setRows(prev => [...prev, { key: Date.now(), karat: '', purity: '', perGram: 0, per10gram: 0 }]);
  };

  const removeRow = (key) => {
    setRows(prev => prev.filter(r => r.key !== key));
  };

  const handleSave = async () => {
    if (rows.length === 0) return messageApi.error('Add at least one purity row');
    if (rows.some(r => !r.purity)) return messageApi.error('Every row needs a purity name');
    setSaving(true);
    try {
      const res = await updateSilverRates(rows.map(({ karat, purity, perGram }) => ({ karat, purity, perGram })));
      messageApi.success('Silver rates saved');
      setDate(res.date);
      setNotConfigured(false);
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to save silver rates');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Karat / Label', dataIndex: 'karat', width: 140,
      render: (v, r) => <input className="ant-input" style={{ width: '100%' }} value={v}
        onChange={(e) => setLabel(r.key, 'karat', e.target.value)} placeholder="e.g. 999" />,
    },
    {
      title: 'Purity Name', dataIndex: 'purity', width: 180,
      render: (v, r) => <input className="ant-input" style={{ width: '100%' }} value={v}
        onChange={(e) => setLabel(r.key, 'purity', e.target.value)} placeholder="e.g. Silver 999" />,
    },
    {
      title: 'Rate per gram (₹)', dataIndex: 'perGram', width: 170,
      render: (v, r) => <InputNumber min={0} precision={2} value={v} style={{ width: '100%' }}
        onChange={(val) => setRate(r.key, val)} prefix="₹" />,
    },
    {
      title: 'Per 10 gm (₹)', dataIndex: 'per10gram', width: 140,
      render: (v) => <Text type="secondary">₹{(v || 0).toLocaleString('en-IN')}</Text>,
    },
    {
      title: '', width: 50,
      render: (_, r) => <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => removeRow(r.key)} />,
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Card
        title={<Title level={5} style={{ margin: 0 }}>Silver Rates (Manual Daily Rate)</Title>}
        extra={
          <Space>
            {date && <Tag color="blue">Last set: {date}</Tag>}
            <Button icon={<ReloadOutlined />} onClick={() => load(true)}>Reload</Button>
            <Button icon={<PlusOutlined />} onClick={addRow}>Add Purity</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save Rates</Button>
          </Space>
        }
      >
        {notConfigured && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message="Silver rates have never been set"
            description="Silver cases cannot price items until today's rates are saved here."
          />
        )}
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message="These rates auto-fill the case forms whenever Metal Type = Silver. Update them daily — there is no live silver feed."
        />
        <Table dataSource={rows} columns={columns} pagination={false} size="small" bordered />
      </Card>
    </div>
  );
}
